// "Featured in the Vibing Library" — the embeddable badge.
// Anyone whose project is on the shelf can copy a snippet from here.

import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteFooter } from "@/components/site-footer";
import { BadgeSnippets } from "@/components/library-badge-copy";
import { Gem } from "@/components/icons";

const TITLE = "Featured in the Vibing Library — badge";
const DESC =
  "Grab the official 'Featured in the Vibing Library' badge for your README, site or landing page. HTML, Markdown and JSX snippets, light and dark.";
const URL = "https://vibing.inc/library/badge";

export const Route = createFileRoute("/library/badge")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: BadgePage,
});

function slugify(v: string) {
  return v
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\/[^/]*\/library\//, "")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function BadgePage() {
  const [raw, setRaw] = useState("");
  const slug = slugify(raw) || "your-project-slug";

  return (
    <div className="min-h-svh bg-background">
      <main className="mx-auto max-w-[1000px] px-6 md:px-10">
        <div className="pt-[clamp(3rem,6vh,5rem)] pb-10">
          <Link
            to="/library"
            className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm text-muted-foreground ring-1 ring-border/60 transition-colors hover:bg-muted/40 hover:text-foreground"
          >
            Back to the library
          </Link>

          <h1
            className="mt-7 flex items-center gap-3 font-display leading-[1.02] tracking-[-0.035em]"
            style={{ fontSize: "clamp(2.2rem, 5vw, 4rem)" }}
          >
            <Gem aria-hidden style={{ width: "0.72em", height: "0.72em" }} />
            Featured? Wear it.
          </h1>
          <p className="mt-5 max-w-[58ch] text-lg leading-snug text-muted-foreground text-pretty">
            If your project is on the shelf, this badge says so — and links straight
            back to your entry. Free to use, no attribution required, no tracking
            beyond a plain referrer.
          </p>
        </div>

        <section className="rounded-3xl bg-card p-6 ring-1 ring-border/60 md:p-8">
          <label htmlFor="slug" className="text-sm">
            Your entry's slug or link
          </label>
          <input
            id="slug"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="https://vibing.inc/library/mesher  ·  or just: mesher"
            className="mt-1.5 w-full rounded-xl bg-background px-3 py-2.5 text-sm ring-1 ring-border/60 placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Using <span className="font-mono">{slug}</span>. Not on the shelf yet?{" "}
            <Link to="/library/submit" className="underline underline-offset-4">
              Submit your project
            </Link>
            .
          </p>

          <div className="mt-8">
            <BadgeSnippets slug={slug} />
          </div>
        </section>

        <section className="mt-12 border-t border-border/60 pt-8 pb-16">
          <h2 className="font-display text-xl tracking-tight">House rules</h2>
          <ul className="mt-4 max-w-[62ch] space-y-2 text-sm text-muted-foreground">
            <li>Only use the badge if your project actually has an entry in the library.</li>
            <li>Keep the link intact — it is what makes the badge honest.</li>
            <li>Do not restyle or recolour the artwork; both themes are provided.</li>
          </ul>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
